import React, { useState, useEffect, useCallback } from 'react';
import { CloudIcon, CloudArrowUpIcon, SignalIcon, ExclamationTriangleIcon, ArrowPathIcon } from './Icons';

interface SyncAction {
    id: string;
    resource: string;
    method: 'POST' | 'PATCH' | 'DELETE';
    data?: any;
    timestamp: number;
    retryCount: number;
}

interface OfflineSyncManagerProps {
    onSyncAction: (action: SyncAction) => Promise<boolean>;
    userId: string | null;
}

const SYNC_QUEUE_KEY = 'edu_sync_queue';

const OfflineSyncManager: React.FC<OfflineSyncManagerProps> = ({ onSyncAction, userId }) => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [syncQueue, setSyncQueue] = useState<SyncAction[]>([]);
    const [isSyncing, setIsSyncing] = useState(false);
    const [showStatus, setShowStatus] = useState(false);

    // Load queue from localStorage on mount
    useEffect(() => {
        if (!userId) return;
        const savedQueue = localStorage.getItem(`${userId}_${SYNC_QUEUE_KEY}`);
        if (savedQueue) {
            try {
                setSyncQueue(JSON.parse(savedQueue));
            } catch (e) {
                console.error("Failed to parse sync queue", e);
            }
        }
    }, [userId]);

    // Save queue to localStorage whenever it changes
    useEffect(() => {
        if (!userId) return;
        localStorage.setItem(`${userId}_${SYNC_QUEUE_KEY}`, JSON.stringify(syncQueue));
    }, [syncQueue, userId]);

    // Listen for online/offline events
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            triggerSync();
        };
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const triggerSync = useCallback(async () => {
        if (!isOnline || syncQueue.length === 0 || isSyncing) return;

        setIsSyncing(true);
        const queue = [...syncQueue];
        const remainingActions: SyncAction[] = [];

        for (const action of queue) {
            try {
                const success = await onSyncAction(action);
                if (!success) {
                    remainingActions.push({
                        ...action,
                        retryCount: action.retryCount + 1
                    });
                }
            } catch (error) {
                console.error("Sync action failed", error);
                remainingActions.push({
                    ...action,
                    retryCount: action.retryCount + 1
                });
            }
        }

        setSyncQueue(remainingActions);
        setIsSyncing(false);
    }, [isOnline, syncQueue, isSyncing, onSyncAction]);

    // Expose a function to add to queue (can be done via window event or ref if needed)
    // For now, we'll use a custom event approach or just App.tsx will pass it down.
    useEffect(() => {
        const handleAddAction = (e: any) => {
            const action: SyncAction = {
                id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                ...e.detail,
                timestamp: Date.now(),
                retryCount: 0
            };
            setSyncQueue(prev => [...prev, action]);
            if (isOnline) triggerSync();
        };

        window.addEventListener('edu_add_sync_action', handleAddAction);
        return () => window.removeEventListener('edu_add_sync_action', handleAddAction);
    }, [isOnline, triggerSync]);

    if (syncQueue.length === 0 && isOnline) return null;

    return (
        <div className="fixed bottom-6 right-6 z-[60]">
            <div className={`flex items-center gap-3 p-3 rounded-2xl shadow-2xl transition-all border-2 ${
                !isOnline ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-teal-100 text-teal-600'
            }`}>
                <div className="flex items-center gap-2">
                    {!isOnline ? (
                        <SignalIcon className="h-5 w-5 animate-pulse text-amber-500" />
                    ) : isSyncing ? (
                        <ArrowPathIcon className="h-5 w-5 animate-spin text-teal-500" />
                    ) : (
                        <CloudArrowUpIcon className="h-5 w-5 text-teal-500" />
                    )}
                    
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black uppercase tracking-widest leading-none">
                            {!isOnline ? 'Đang ngoại tuyến' : isSyncing ? 'Đang đồng bộ' : 'Đã kết nối'}
                        </span>
                        {syncQueue.length > 0 && (
                            <span className="text-[9px] font-bold opacity-70">
                                {syncQueue.length} thay đổi chờ gửi
                            </span>
                        )}
                    </div>
                </div>

                {!isOnline && (
                    <div className="h-8 w-px bg-amber-200 mx-1" />
                )}

                {!isOnline ? (
                    <div className="flex items-center gap-2 px-2 py-1 bg-amber-100 rounded-lg">
                        <ExclamationTriangleIcon className="h-3 w-3" />
                        <span className="text-[9px] font-bold">Làm việc offline</span>
                    </div>
                ) : syncQueue.length > 0 && !isSyncing ? (
                    <button 
                        onClick={triggerSync}
                        className="px-3 py-1.5 bg-teal-600 text-white text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-teal-700 transition-all shadow-lg shadow-teal-100"
                    >
                        Đồng bộ ngay
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export default OfflineSyncManager;

export const queueOfflineAction = (resource: string, method: 'POST' | 'PATCH' | 'DELETE', data?: any) => {
    window.dispatchEvent(new CustomEvent('edu_add_sync_action', {
        detail: { resource, method, data }
    }));
};
